<template>
  <!-- The auto-attendant settings in Workspace369 (Settings → Email, SMS & phone → IVR
       auto-attendant; lib/view/inbox/settings/ivr_settings.dart, ivr_option_row.dart,
       ivr_caller_preview.dart). Strings are the app's (settingsCommsVoiceIvr*): the enable switch,
       the greeting, the menu options mapped to a ring group or voicemail, and the "What callers
       hear" card. Ring group names and the greeting are sample data. Inside a StoryScene the
       switch turns on, the options appear one by one and the caller preview fills in; resting
       styles are the final frame. -->
  <div class="ivw" aria-hidden="true">
    <header class="ivw-head">
      <b>IVR auto-attendant</b>
      <small>+1 (512) 555-0103</small>
    </header>
    <div class="ivw-body">
      <div class="ivw-row ivw-in" style="--d: 0.1s">
        <span class="ivw-row-copy">
          <b>Enable auto-attendant</b>
          <small>Active — callers hear the menu first.</small>
        </span>
        <span class="ivw-switch"><i /></span>
      </div>

      <div class="ivw-field ivw-in" style="--d: 0.5s">
        <small>Greeting</small>
        <p>Thanks for calling Ortiz Studio. For bookings press 1, for support press 2, to leave a message press 3.</p>
      </div>

      <p class="ivw-label ivw-in" style="--d: 0.8s">Menu options</p>
      <ol class="ivw-options">
        <li v-for="(o, i) in options" :key="o.digit" class="ivw-in" :style="{ '--d': `${1.1 + i * 0.45}s` }">
          <span class="ivw-digit">{{ o.digit }}</span>
          <span class="ivw-route">
            <svg viewBox="0 0 24 24"><path :d="o.voicemail ? icons.voicemail : icons.group" /></svg>
            {{ o.route }}
          </span>
          <span class="ivw-tag">{{ o.label }}</span>
        </li>
      </ol>

      <div class="ivw-preview ivw-in" style="--d: 2.6s">
        <p class="ivw-label">What callers hear</p>
        <p class="ivw-quote"><svg viewBox="0 0 24 24"><path :d="icons.quote" /></svg>“Thanks for calling Ortiz Studio. For bookings press 1, for support press 2...”</p>
        <p v-for="o in options" :key="o.digit" class="ivw-say"><span class="ivw-digit">{{ o.digit }}</span>{{ o.preview }}</p>
        <p class="ivw-nokey">No keypress → routes to your default ring group.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
const options = [
  { digit: "1", route: "Ring “Bookings”", label: "Bookings", preview: "Rings “Bookings” (3 members) — Bookings" },
  { digit: "2", route: "Ring “Support”", label: "Support", preview: "Rings “Support” (2 members) — Support" },
  { digit: "3", route: "Send to voicemail", label: "Message", preview: "Sends the caller to voicemail — Message", voicemail: true },
];
const icons = {
  group: "M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
  voicemail: "M18.5 6C15.46 6 13 8.46 13 11.5c0 1.33.47 2.55 1.26 3.5H9.74c.79-.95 1.26-2.17 1.26-3.5C11 8.46 8.54 6 5.5 6S0 8.46 0 11.5 2.46 17 5.5 17h13c3.04 0 5.5-2.46 5.5-5.5S21.54 6 18.5 6zm-13 9C3.57 15 2 13.43 2 11.5S3.57 8 5.5 8 9 9.57 9 11.5 7.43 15 5.5 15zm13 0c-1.93 0-3.5-1.57-3.5-3.5S16.57 8 18.5 8 22 9.57 22 11.5 20.43 15 18.5 15z",
  quote: "M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z",
};
</script>

<style>
.ivw {
  --ivw-page: #fff;
  --ivw-wash: #f4f6f8;
  --ivw-line: rgb(0 0 0 / 0.1);
  --ivw-text: #171717;
  --ivw-muted: #5f6368;
  --ivw-sunken: #eef0f3;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--ivw-line);
  border-radius: 10px;
  background: var(--ivw-wash);
  color: var(--ivw-text);
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.28), 0 2px 6px rgb(15 23 42 / 0.06);
}
.dark .ivw {
  --ivw-page: #1c1c1c;
  --ivw-wash: #242424;
  --ivw-line: rgb(255 255 255 / 0.13);
  --ivw-text: #ececec;
  --ivw-muted: #a8a8a8;
  --ivw-sunken: #2f2f2f;
  box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.7);
}
.ivw svg { width: 14px; height: 14px; flex: none; fill: currentColor; }
.ivw-head { flex: none; display: flex; align-items: baseline; justify-content: space-between; gap: 8px; border-bottom: 1px solid var(--ivw-line); background: var(--ivw-page); padding: 11px 14px; }
.ivw-head b { font-size: 14px; font-weight: 600; }
.ivw-head small { color: var(--ivw-muted); font-size: 11.5px; }
.ivw-body { display: grid; flex: 1; align-content: start; gap: 8px; min-height: 0; overflow: hidden; padding: 12px 14px; }
.ivw-row, .ivw-field, .ivw-options li, .ivw-preview { border: 1px solid var(--ivw-line); border-radius: 10px; background: var(--ivw-page); }
.ivw-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 8px 11px; }
.ivw-row-copy { display: grid; }
.ivw-row b { font-weight: 600; }
.ivw-row small, .ivw-field small { color: var(--ivw-muted); font-size: 11.5px; }
.ivw-switch { position: relative; flex: none; width: 36px; height: 21px; border-radius: 999px; background: #171717; }
.ivw-switch i { position: absolute; top: 3px; left: 18px; width: 15px; height: 15px; border-radius: 999px; background: #fff; }
.dark .ivw-switch { background: #ececec; }
.dark .ivw-switch i { background: #171717; }
.ivw-field { padding: 7px 11px; }
.ivw-field p { margin: 1px 0 0; font-size: 12.5px; }
.ivw-label { margin: 2px 0 -2px; color: var(--ivw-muted); font-size: 11.5px; font-weight: 600; }
.ivw-options { display: grid; gap: 5px; margin: 0; padding: 0; list-style: none; }
.ivw-options li { display: flex; align-items: center; gap: 9px; padding: 6px 10px; }
.ivw-digit { display: inline-grid; place-items: center; flex: none; width: 22px; height: 22px; border-radius: 6px; background: var(--ivw-sunken); font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; }
.ivw-route { display: inline-flex; flex: 1; align-items: center; gap: 6px; min-width: 0; font-size: 12.5px; }
.ivw-tag { color: var(--ivw-muted); font-size: 11.5px; }
.ivw-preview { display: grid; gap: 5px; padding: 8px 11px; }
.ivw-preview p { margin: 0; }
.ivw-preview .ivw-label { margin: 0 0 2px; }
.ivw-quote { display: flex; gap: 7px; font-size: 12.5px; font-style: italic; }
.ivw-quote svg { margin-top: 2px; color: var(--ivw-muted); }
.ivw-say { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.ivw-nokey { color: var(--ivw-muted); font-size: 11px; font-style: italic; }

.is-run .ivw-in { animation: ivw-in 0.5s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .ivw-switch { animation: ivw-track 0.3s ease 0.4s both; }
.is-run .ivw-switch i { animation: ivw-knob 0.3s ease 0.4s both; }
@keyframes ivw-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes ivw-track { from { background: #c7cbd1; } }
@keyframes ivw-knob { from { transform: translateX(-15px); } to { transform: none; } }
/* The settings and the caller preview need a taller stage than other scenes. The :not() lifts these
   above the stages' own `.sst:not(.sst--app)` heights, which otherwise win whenever their CSS loads later. */
.sst:not(.sst--app):has(.ivw) { height: 640px; }
@media (max-width: 639px) {
  .sst:not(.sst--app):has(.ivw) { height: 600px; }
}
@media (max-width: 480px) {
  .ivw-body { padding: 10px; gap: 6px; }
  .ivw-tag { display: none; }
  /* The greeting is quoted again in the caller preview, so phones skip the field. It is taken out
     of the flow rather than set to display: none, which would restart its entrance animation
     whenever the width crosses this line. */
  .ivw-field { position: absolute; visibility: hidden; }
}
</style>
